/**
 * Compact controls for scenario-style theorem demos.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { FONT, GRAY, PRIMARY, SAFE, WARN } from "../design-system/index.js";

export type DemoScenarioTone = "danger" | "neutral" | "safe" | "warn" | "primary";

const TONE = {
  danger: { bg: WARN[100], border: WARN[500], fg: WARN[700] },
  neutral: { bg: "white", border: GRAY[300], fg: GRAY[700] },
  primary: { bg: PRIMARY[100], border: PRIMARY[300], fg: PRIMARY[700] },
  safe: { bg: SAFE[100], border: SAFE[300], fg: SAFE[700] },
  warn: { bg: WARN[100], border: WARN[300], fg: WARN[700] },
} as const;

export interface DemoScenarioCardProps {
  readonly children: React.ReactNode;
  readonly className?: string;
  readonly style?: React.CSSProperties;
}

export function DemoScenarioCard({
  children,
  className,
  style,
}: DemoScenarioCardProps): React.JSX.Element {
  return (
    <div
      className={className}
      style={{
        background: "white",
        border: `1px solid ${GRAY[300]}`,
        borderRadius: 8,
        padding: "1rem",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export interface DemoScenarioActionButtonProps {
  readonly children: React.ReactNode;
  readonly disabled?: boolean;
  readonly fullWidth?: boolean;
  readonly icon?: React.ReactNode;
  readonly onClick: () => void;
  readonly style?: React.CSSProperties;
  readonly tone?: DemoScenarioTone;
}

export function DemoScenarioActionButton({
  children,
  disabled = false,
  fullWidth = true,
  icon,
  onClick,
  style,
  tone = "primary",
}: DemoScenarioActionButtonProps): React.JSX.Element {
  const color = disabled
    ? GRAY[300]
    : tone === "danger"
      ? WARN[700]
      : tone === "warn"
      ? WARN[500]
      : tone === "safe"
        ? SAFE[500]
        : PRIMARY[500];
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      style={{
        alignItems: "center",
        backgroundColor: color,
        border: "none",
        borderRadius: 8,
        color: "white",
        cursor: disabled ? "not-allowed" : "pointer",
        display: "inline-flex",
        fontWeight: 700,
        gap: "0.4rem",
        justifyContent: "center",
        marginTop: "1rem",
        minHeight: 38,
        padding: "0.6rem 1rem",
        width: fullWidth ? "100%" : "auto",
        ...style,
      }}
    >
      {icon}
      {children}
    </button>
  );
}

export interface DemoScenarioItemButtonProps {
  readonly badge?: React.ReactNode;
  readonly children?: React.ReactNode;
  readonly detail?: React.ReactNode;
  readonly icon?: React.ReactNode;
  readonly label: React.ReactNode;
  readonly onClick: () => void;
  readonly selected?: boolean;
  readonly tone?: DemoScenarioTone;
}

export function DemoScenarioItemButton({
  badge,
  children,
  detail,
  icon,
  label,
  onClick,
  selected = false,
  tone = "neutral",
}: DemoScenarioItemButtonProps): React.JSX.Element {
  const palette = selected ? TONE.primary : TONE[tone];
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        alignItems: "center",
        background: palette.bg,
        border: `2px solid ${selected ? PRIMARY[500] : palette.border}`,
        borderRadius: 8,
        cursor: "pointer",
        display: "flex",
        gap: "0.75rem",
        padding: "0.75rem 1rem",
        textAlign: "left",
        width: "100%",
      }}
    >
      {icon ? <span style={{ color: palette.fg, flexShrink: 0 }}>{icon}</span> : null}
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ color: GRAY[900], display: "block", fontWeight: 700 }}>{label}</span>
        {detail ? (
          <span style={{ color: GRAY[500], display: "block", fontSize: "0.75rem" }}>{detail}</span>
        ) : null}
        {children}
      </span>
      {badge}
    </button>
  );
}

export function DemoScenarioList({
  children,
}: {
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>{children}</div>;
}

export function DemoScenarioToolbar({
  children,
  className,
  style,
}: {
  readonly children: React.ReactNode;
  readonly className?: string;
  readonly style?: React.CSSProperties;
}): React.JSX.Element {
  return (
    <div
      className={className}
      style={{
        alignItems: "center",
        display: "flex",
        flexWrap: "wrap",
        gap: "0.5rem",
        marginTop: "0.75rem",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function DemoScenarioField({
  label,
  children,
  className,
  style,
}: {
  readonly label: React.ReactNode;
  readonly children: React.ReactNode;
  readonly className?: string;
  readonly style?: React.CSSProperties;
}): React.JSX.Element {
  return (
    <label
      className={className}
      style={{
        color: GRAY[900],
        display: "block",
        flex: 1,
        fontSize: "0.875rem",
        fontWeight: 700,
        minWidth: 0,
        ...style,
      }}
    >
      {label}
      {children}
    </label>
  );
}

export function DemoScenarioNumberInput({
  value,
  onChange,
  min,
  max,
  step,
  ariaLabel,
}: {
  readonly value: string | number;
  readonly onChange: (next: string) => void;
  readonly min?: number;
  readonly max?: number;
  readonly step?: number;
  readonly ariaLabel?: string;
}): React.JSX.Element {
  return (
    <input
      type="number"
      min={min}
      max={max}
      step={step}
      aria-label={ariaLabel}
      value={value}
      onChange={(event) => {
        onChange(event.target.value);
      }}
      style={{
        border: `1px solid ${GRAY[300]}`,
        borderRadius: 8,
        display: "block",
        fontSize: "1rem",
        marginTop: 4,
        padding: "0.5rem",
        width: "100%",
      }}
    />
  );
}

export function DemoScenarioPresetChips<TValue extends string | number>({
  values,
  onSelect,
  format = String,
  tone = "primary",
}: {
  readonly values: readonly TValue[];
  readonly onSelect: (value: TValue) => void;
  readonly format?: (value: TValue) => React.ReactNode;
  readonly tone?: DemoScenarioTone;
}): React.JSX.Element {
  return (
    <DemoScenarioToolbar>
      {values.map((value) => (
        <DemoScenarioActionButton
          key={String(value)}
          fullWidth={false}
          onClick={() => {
            onSelect(value);
          }}
          tone={tone}
          style={{ marginTop: 0, minHeight: 28, padding: "0.25rem 0.625rem" }}
        >
          {format(value)}
        </DemoScenarioActionButton>
      ))}
    </DemoScenarioToolbar>
  );
}

export function DemoScenarioStatRail({
  items,
}: {
  readonly items: readonly {
    readonly id: string;
    readonly label: React.ReactNode;
    readonly value: React.ReactNode;
    readonly tone?: DemoScenarioTone;
  }[];
}): React.JSX.Element {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
      {items.map((item) => {
        const palette = TONE[item.tone ?? "neutral"];
        return (
          <div
            key={item.id}
            style={{
              background: palette.bg,
              border: `1px solid ${palette.border}`,
              borderRadius: 8,
              minWidth: 92,
              padding: "0.55rem 0.7rem",
            }}
          >
            <div style={{ color: palette.fg, fontSize: "1rem", fontWeight: 800 }}>
              {item.value}
            </div>
            <div style={{ color: GRAY[500], fontSize: "0.7rem", fontWeight: 700 }}>
              {item.label}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function DemoScenarioBadge({
  children,
  tone = "neutral",
}: {
  readonly children: React.ReactNode;
  readonly tone?: DemoScenarioTone;
}): React.JSX.Element {
  const palette = TONE[tone];
  return (
    <span
      style={{
        background: palette.bg,
        borderRadius: 6,
        color: palette.fg,
        fontSize: "0.7rem",
        fontWeight: 800,
        padding: "0.125rem 0.45rem",
        textTransform: "uppercase",
      }}
    >
      {children}
    </span>
  );
}

export function DemoScenarioProbe({
  children,
  tone,
}: {
  readonly children: React.ReactNode;
  readonly tone: Exclude<DemoScenarioTone, "neutral" | "primary">;
}): React.JSX.Element {
  const palette = TONE[tone];
  return (
    <div
      style={{
        background: palette.bg,
        border: `1px solid ${palette.border}`,
        borderRadius: 8,
        color: palette.fg,
        fontFamily: FONT.mono,
        fontSize: "0.8125rem",
        marginTop: "0.75rem",
        padding: "0.625rem 0.875rem",
      }}
    >
      {children}
    </div>
  );
}

export function DemoScenarioMeter({
  label,
  value,
  max,
  color,
  detail,
}: {
  readonly label: React.ReactNode;
  readonly value: number;
  readonly max: number;
  readonly color: string;
  readonly detail?: React.ReactNode;
}): React.JSX.Element {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <div style={{ marginBottom: "1.5rem" }}>
      <div
        style={{
          alignItems: "center",
          display: "flex",
          gap: "0.5rem",
          justifyContent: "space-between",
          marginBottom: "0.25rem",
        }}
      >
        <span style={{ alignItems: "center", display: "flex", gap: "0.375rem", fontWeight: 700 }}>
          {label}
        </span>
        <span style={{ color: value === 0 ? WARN[700] : GRAY[700] }}>
          {value} / {max}
        </span>
      </div>
      <div
        style={{ background: GRAY[100], borderRadius: 999, height: "1.25rem", overflow: "hidden" }}
      >
        <div
          style={{
            backgroundColor: color,
            height: "100%",
            transition: "width 0.3s, background-color 0.3s",
            width: `${pct}%`,
          }}
        />
      </div>
      {detail ? (
        <div
          style={{
            color: GRAY[500],
            fontFamily: FONT.mono,
            fontSize: "0.75rem",
            marginTop: "0.25rem",
          }}
        >
          {detail}
        </div>
      ) : null}
    </div>
  );
}
